import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { getCookie, setCookie, removeCookie } from "@/lib/cookies";

type Theme = "dark" | "light" | null; // null = follow system preference
type ResolvedTheme = "dark" | "light";

const THEME_COOKIE_NAME = "vite-ui-theme";
const THEME_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

type ThemeProviderProps = {
  children: React.ReactNode;
  storageKey?: string;
};

type ThemeProviderState = {
  resolvedTheme: ResolvedTheme;
  theme: Theme;
  setTheme: (theme: ResolvedTheme) => void;
  resetTheme: () => void;
};

const initialState: ThemeProviderState = {
  resolvedTheme: "light",
  theme: null,
  setTheme: () => null,
  resetTheme: () => null,
};

const ThemeContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  storageKey = THEME_COOKIE_NAME,
  ...props
}: ThemeProviderProps) {
  const [theme, _setTheme] = useState<Theme>(() => {
    const storedTheme = getCookie(storageKey) as ResolvedTheme;
    // Only return stored theme if it's a valid explicit choice (light/dark)
    return storedTheme === "light" || storedTheme === "dark"
      ? storedTheme
      : null;
  });

  // Optimized: Memoize the resolved theme calculation to prevent unnecessary re-computations
  const resolvedTheme = useMemo((): ResolvedTheme => {
    if (theme === null) {
      // Follow system preference when no explicit choice is made
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
    return theme;
  }, [theme]);

  useEffect(() => {
    const root = window.document.documentElement;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const applyTheme = (currentResolvedTheme: ResolvedTheme) => {
      root.classList.remove("light", "dark"); // Remove existing theme classes
      root.classList.add(currentResolvedTheme); // Add the new theme class
    };

    const handleChange = () => {
      if (theme === null) {
        // Only respond to system changes when no explicit choice is made
        const systemTheme = mediaQuery.matches ? "dark" : "light";
        applyTheme(systemTheme);
      }
    };

    applyTheme(resolvedTheme);

    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme, resolvedTheme]);

  const setTheme = (newTheme: ResolvedTheme) => {
    // Store explicit user choice in cookie
    setCookie(storageKey, newTheme, THEME_COOKIE_MAX_AGE);
    _setTheme(newTheme);
  };

  const resetTheme = () => {
    // Remove cookie and return to system preference following
    removeCookie(storageKey);
    _setTheme(null);
  };

  const contextValue = {
    resolvedTheme,
    resetTheme,
    theme,
    setTheme,
  };

  return (
    <ThemeContext value={contextValue} {...props}>
      {children}
    </ThemeContext>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (!context) throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
